import {ALL_PERMISSIONS, type Permission} from "./RightsSystem.ts";

const permissionMap = new Map<Permission, string>();

const setPermission = (permission: Permission, value: string) => {
    permissionMap.set(permission, value);
};

setPermission("Garage_read", "servicelister.garage.read");
setPermission("Garage_list", "servicelister.garage.list");
setPermission("GarageGroup_list", "servicelister.garagegroup.list");
setPermission("Garage_write", "servicelister.garage.write");
setPermission("Garage_Address_write", "servicelister.garage.create");
setPermission("Garage_GeoLocation_write", "servicelister.garage.geolocation.write");
setPermission("Garage_SalesCountry_write", "servicelister.garage.write");

setPermission("Coupon_list", "servicelister.coupon.list");
setPermission("Coupon_read", "servicelister.coupon.read");
setPermission("Coupon_write", "servicelister.coupon.write");
setPermission("Coupon_create", "servicelister.coupon.write");

setPermission("Customer_list", "cockpit.customer.list");
setPermission("Customer_read", "cockpit.customer.read");
setPermission("Customer_write", "cockpit.customer.write");
setPermission("Customer_create", "cockpit.customer.write");
setPermission("Customer_delete", "cockpit.customer.delete");

setPermission("MailTemplates_read", "cockpit.mail-customization.read");
setPermission("MailTemplates_write", "cockpit.mail-customization.write");

setPermission("SupportContext_write", "cockpit.support-context.write");

setPermission("MailServer_read", "cockpit.mail-server.read");
setPermission("MailServer_write", "cockpit.mail-server.write");

setPermission("PriceBook_read", "cockpit.price-book.read");
setPermission("PriceBook_write", "cockpit.price-book.write");

setPermission("Promotion_list", "cockpit.promotion.read");
setPermission("Promotion_read", "cockpit.promotion.read");
setPermission("Promotion_write", "cockpit.promotion.write");

setPermission("DealerProduct_list", "servicelister.offering.dealerproduct.list");
setPermission("DealerProduct_read", "servicelister.offering.dealerproduct.read");
setPermission("DealerProduct_write", "servicelister.offering.dealerproduct.write");
setPermission("DealerProduct_MassUpdate_write", "servicelister.offering.dealerproduct.write");
setPermission("DealerProduct_Republish_write", "servicelister.offering.dealerproduct.publish");

setPermission("GarageProduct_read", "servicelister.offering.dealerproduct.read");
// Backend currently bundles create + write under .write — anyone allowed to
// edit a product can also create one. Revisit if a dedicated .create scope
// ever lands server-side.
setPermission("GarageProduct_create", "servicelister.offering.dealerproduct.write");
setPermission("GarageProduct_write", "servicelister.offering.dealerproduct.write");
setPermission("GarageProduct_delete", "servicelister.offering.dealerproduct.delete");

setPermission("ProductCategories_list", "servicelister.offering.dealerproduct.write");
setPermission("ProductCategories_read", "servicelister.offering.dealerproduct.write");
// See note above — bundled with .write on the backend.
setPermission("ProductCategories_create", "servicelister.offering.dealerproduct.write");
setPermission("ProductCategories_write", "servicelister.offering.dealerproduct.write");
setPermission("ProductCategories_delete", "servicelister.offering.dealerproduct.delete");

setPermission("Order_list", "servicelister.order.list");
setPermission("Order_read", "servicelister.order.read");
setPermission("Order_write", "servicelister.order.write");

setPermission("User_read", "cockpit.user.read");
setPermission("User_create", "cockpit.user.create");
setPermission("User_write", "cockpit.user.write");
setPermission("User_list", "cockpit.user.list");
setPermission("User_delete", "cockpit.user.delete");

setPermission("Contract_list", "cockpit.contracting.list");
setPermission("Contract_read", "cockpit.contracting.read");
setPermission("Contract_create", "cockpit.contracting.create");
setPermission("Contract_write", "cockpit.contracting.write");

setPermission("DPA_create", "servicelister.dpa.create");
setPermission("DPA_read", "servicelister.dpa.read");
setPermission("DPA_write", "servicelister.dpa.create");
setPermission("DPA_delete", "servicelister.dpa.delete");

setPermission("Dashboard_read", "servicelister.dashboard.read");

const SUPERADMIN = "ROLE_S_U_P_E_R_A+D+M+I+N";

export class RightsMapper {
    static getMapping = (permission: Permission): string => {
        const slRight = permissionMap.get(permission);
        if (!slRight) {
            throw new Error(`no mapping for required permission ${permission} found!`);
        }
        return slRight;
    };

    static hasMapping = (permission: Permission): boolean => {
        return permissionMap.has(permission);
    };

    static getUserPermissions (authorities: string[]): Permission[] {
        if (this.isSuperAdmin(authorities)) return ALL_PERMISSIONS;

        return ALL_PERMISSIONS.filter(permission => {
            if (this.hasMapping(permission)) {
                return authorities.includes(this.getMapping(permission));
            }
            return false;
        });
    }

    static isSuperAdmin (authorities: string[]): boolean {
        return authorities.includes(SUPERADMIN);
    }
}
